import { useMemo } from 'react'
import { useLocation } from 'react-router-dom'

import { OneCreateActionsDependenciesDocument } from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'
import { manifests } from 'modules/core/lib/manifests/manager'
import { DomainManifest } from 'modules/core/lib/manifests/types'

import { OneCreateAction } from './types'

function isValidManifestSection(section: string): section is keyof typeof manifests {
  return section in manifests
}

function isValidOneCreateAction(
  oneManifest: NonNullable<DomainManifest['one']>,
  createAction: string
): createAction is keyof typeof oneManifest.createActions {
  return !!oneManifest.createActions && createAction in oneManifest.createActions
}

export function findOneCreateAction(path: string): OneCreateAction | null {
  const [section, createAction] = path.split('.')

  if (!section || !createAction || !isValidManifestSection(section)) {
    return null
  }

  const manifest = manifests[section]

  if (
    !manifest ||
    !('one' in manifest) ||
    !('createActions' in manifest.one) ||
    !isValidOneCreateAction(manifest.one, createAction)
  ) {
    return null
  }

  return manifest.one.createActions[createAction]
}

export function useOneActionNames(): string[] {
  const createActionDefinitions = useMemo(() => {
    const definitions: { path: string; createAction: OneCreateAction }[] = []

    Object.entries(manifests).forEach(([section, manifest]) => {
      if (manifest && 'one' in manifest && 'createActions' in manifest.one) {
        Object.entries(manifest.one.createActions).forEach(([action, createAction]) => {
          definitions.push({
            path: `${section}.${action}`,
            createAction: {
              ...createAction,
              policies: Array.isArray(createAction.policies) ? [...createAction.policies] : [],
            },
          })
        })
      }
    })

    return definitions
  }, [])

  const processedCreateActions = createActionDefinitions.map(({ path, createAction }) => {
    return {
      path,
      module: createAction.module,
      policies: createAction.policies ?? [],
    }
  })

  const { moduleIds, policies } = useMemo(() => {
    const moduleSet = new Set<string>()
    const policySet = new Set<string>()

    processedCreateActions.forEach(({ module, policies: actionPolicies }) => {
      if (module) {
        moduleSet.add(module)
      }
      actionPolicies.forEach((policy) => policySet.add(policy))
    })

    return {
      moduleIds: Array.from(moduleSet),
      policies: Array.from(policySet),
    }
  }, [processedCreateActions])

  const { data } = useQuery(OneCreateActionsDependenciesDocument, {
    variables: {
      moduleIds,
      includeModules: moduleIds.length > 0,
      policies,
      includePolicies: policies.length > 0,
    },
  })

  const availableModules = useMemo(() => {
    return new Set(data?.apiCore?.moduleRegistries?.map((module) => module.id) ?? [])
  }, [data])

  const allowedPolicyIds = useMemo(() => {
    return new Set(
      (data?.permissions?.contextlessPolicyResolutions ?? [])
        .filter((policy) => policy.can)
        .map((policy) => policy.id)
    )
  }, [data])

  return useMemo(() => {
    return processedCreateActions.flatMap(({ module, policies: policyNames, path }) => {
      const moduleAvailable = module === undefined || availableModules.has(module)
      if (!moduleAvailable) return []

      const can =
        policyNames.length === 0 || policyNames.every((policy) => allowedPolicyIds.has(policy))

      return can ? [path] : []
    })
  }, [processedCreateActions, availableModules, allowedPolicyIds])
}

export function useOneCreateActionState<T>(): { data: T | undefined; threadId: string } {
  const location = useLocation()

  return useMemo(() => {
    if (!location.state || typeof location.state !== 'object') {
      return { data: undefined, threadId: '' }
    }

    const { oneCreationData, threadId } = location.state

    return { data: oneCreationData, threadId }
  }, [location.state])
}
